import classNames from "classnames";
import React from "react";
import { BasketMenuIcon } from "tsi-common-react/src/apps/checkout/BasketMenuIcon/index";
import { BasketLineVariant } from "tsi-common-react/src/apps/checkout/constants";
import { AccountMenu } from "tsi-common-react/src/apps/customer-account/AccountMenu/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RichNavigation } from "tsi-common-react/src/components/RichNavigation/index";
import { useMobileMenuDialog } from "tsi-common-react/src/hooks/useMobileMenuDialog";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { useCurrentBreakpoint } from "tsi-common-react/src/utils/hooks";
import {
    isPageInsideIFrame as isPageInsideIFrameCheck,
    usePageSetting,
} from "tsi-common-react/src/utils/settings";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { usePageURL, useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Context, reverse } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { FinancingModalTriggerMinicart } from "../../apps/checkout/FinancingModalTriggerMinicart";
import { MainMenu } from "../MainMenu";

import burgerIconStyles from "../../styles/composable/burger-icon.module.scss";
import styles from "./index.module.scss";

type ISiteHeaderProps = React.ContextType<
    typeof Context
>["chrome"]["site_header"];

export const SiteHeader = (props: ISiteHeaderProps) => {
    const isSSR = useIsSSR();
    const enableBasketPrequalSDK = usePageSetting("show_prequal_cart") || false;
    const basketURL = isoWebPageURL.wrap(reverse("basket:summary"));
    const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
    const retailURL = usePageURL("find_a_retailer");
    const isPageInsideIFrame = isSSR ? false : isPageInsideIFrameCheck();
    const viewport = useCurrentBreakpoint();
    const staticURL = useStaticURL();

    const {
        siteHeaderRef,
        mobilePanelVisible,
        showMobileMenuDialog,
        openMobilePanel,
        closeMobilePanel,
    } = useMobileMenuDialog({
        belowMobile: viewport.belowMobile,
        hasRichNavigation: !!props.rich_navigation,
    });

    const mobileRichNavigation =
        showMobileMenuDialog && props.rich_navigation
            ? props.rich_navigation
            : null;

    return (
        <>
            <a className={styles.screenReaderSkip} href="#main-content">
                {t`Skip to main content`}
            </a>
            {viewport.aboveSmall && (
                <div
                    className={styles.promoBanner}
                    role="region"
                    aria-label={t`Promotion banner`}
                >
                    <div className={styles.wrapper}>
                        {props.promo_messaging_banner && (
                            <RichTextBlock
                                value={
                                    props.promo_messaging_banner.promo_message
                                        ?.html || ""
                                }
                            />
                        )}
                        <div>
                            <Link
                                className={styles.retailLink}
                                href={retailURL}
                                title={t`Find a Retailer`}
                                target="_top"
                            >
                                <svg className={styles.retailIcon}>
                                    <title>{t`Find a Retailer Icon`}</title>
                                    <use xlinkHref="#icon-pin" />
                                </svg>
                                <span className="is-desktop">{t`Find a Retailer`}</span>
                            </Link>
                        </div>
                    </div>
                </div>
            )}
            <header
                className={styles.root}
                ref={siteHeaderRef}
                role={showMobileMenuDialog ? "dialog" : undefined}
                aria-modal={showMobileMenuDialog ? true : undefined}
                aria-labelledby={
                    showMobileMenuDialog ? "mobile-panel-title" : undefined
                }
            >
                {showMobileMenuDialog && (
                    <span
                        id="mobile-panel-title"
                        className="ada-screenreader-only"
                    >
                        {t`Menu`}
                    </span>
                )}
                <nav className={styles.wrapper}>
                    {/*
                     * The hamburger stays unconditional so its mobile
                     * `flex-grow` layout holds on pages with no active rich
                     * navigation; `openMobilePanel` no-ops when there is none.
                     */}
                    <button
                        type="button"
                        className={classNames("mobile-toggle", {
                            open: mobilePanelVisible,
                        })}
                        data-react-mobile-nav-trigger={
                            mobilePanelVisible ? undefined : ""
                        }
                        onClick={
                            mobilePanelVisible
                                ? closeMobilePanel
                                : (event) =>
                                      openMobilePanel(event.currentTarget)
                        }
                        aria-controls={
                            mobilePanelVisible ? "mobile-panel" : undefined
                        }
                        aria-expanded={mobilePanelVisible}
                    >
                        <span className="ada-screenreader-only">
                            {t`Expand and collapse the navigation menu`}
                        </span>
                        <span
                            className={concatClassNames([
                                burgerIconStyles.burgerIcon,
                                mobilePanelVisible
                                    ? burgerIconStyles.burgerIconOpen
                                    : null,
                            ])}
                        ></span>
                    </button>
                    {/*
                     * A360 issue-300: the mobile menu must follow its trigger in
                     * DOM order so screen-reader / keyboard users reach it next.
                     * Rendering it here (immediately after the toggle, inside the
                     * ref'd header the dialog focus-trap owns) satisfies that
                     * without the imperative relocation the legacy widget needed.
                     * It is `position: fixed` on mobile and only mounts while
                     * open, so its flow position does not affect layout.
                     */}
                    {mobileRichNavigation && (
                        <div className="rich-navigation" id="mobile-panel">
                            <button
                                type="button"
                                className="mobile-toggle"
                                onClick={closeMobilePanel}
                            >
                                <img
                                    src={`${staticURL}tsicommon_cms/rich-navigation/x-close.svg`}
                                    alt={t`Rich Navigation Toggle`}
                                />
                            </button>
                            <RichNavigation
                                openMobileContentDefaultState={true}
                                {...mobileRichNavigation}
                                enableValuePropsOnMobile
                            />
                            <div className="banner-content" />
                        </div>
                    )}
                    <a href="/" title={t`Sealy Home`} target="_top">
                        {viewport.aboveMobile && (
                            <svg
                                className={styles.desktopLogo}
                                role="img"
                                aria-labelledby="desktop-only-logo"
                            >
                                <title id="desktop-only-logo">
                                    {t`Sealy Homepage`}
                                </title>
                                <use xlinkHref="#bowtie" />
                            </svg>
                        )}
                        {viewport.belowMobile && (
                            <svg
                                className={styles.mobileLogo}
                                role="img"
                                aria-labelledby="mobile-only-logo"
                            >
                                <title id="mobile-only-logo">
                                    {t`Sealy Homepage`}
                                </title>
                                <use xlinkHref="#bowtie" />
                            </svg>
                        )}
                    </a>
                    <div className={styles.nav}>
                        {!props.desktop_rich_navigation && (
                            <MainMenu menuItems={JSON.parse(props.nav_menu)} />
                        )}
                        {props.desktop_rich_navigation &&
                            !viewport.belowMobile && (
                                <RichNavigation
                                    {...props.desktop_rich_navigation}
                                />
                            )}
                    </div>
                    <div className={styles.secondNav}>
                        {viewport.belowMobile && (
                            <Link
                                href={retailURL}
                                title={t`Find a Retailer`}
                                target="_top"
                            >
                                <svg className={styles.mobileRetailIcon}>
                                    <title>{t`Find a Retailer Icon`}</title>
                                    <use xlinkHref="#icon-pin" />
                                </svg>
                            </Link>
                        )}
                        <AccountMenu suppressTriggerDisclosureAria={true} />
                        <BasketMenuIcon
                            className={styles.basketLink}
                            basketURL={basketURL}
                            checkoutURL={checkoutURL}
                            isPageInsideIFrame={isPageInsideIFrame}
                            basketLineVariant={
                                BasketLineVariant.MINIMAL_ENHANCED
                            }
                            buildPromoComponents={(closeModal) => {
                                if (!enableBasketPrequalSDK) {
                                    return null;
                                }
                                return (
                                    <FinancingModalTriggerMinicart
                                        closeModal={closeModal}
                                    />
                                );
                            }}
                            isSSR={isSSR}
                        />
                    </div>
                </nav>
            </header>
        </>
    );
};
